import {UseMutationResult} from '@tanstack/react-query'
import {createContext} from 'react'

import {
  AvailableTimeSlotsResult,
  SingleSlotParams,
  SlotAvailabilityQueryFn,
  SlotValidationQueryFn,
  SlotValidationResponse,
  SlotValidationResult
} from '../requests/types'
import {SlotManagementConfig} from '../types'

export type SetSlotAvailabilityOptions = ({dateTime: string} | {dateTimeIndex: number}) & {
  isAvailable: boolean
}

export interface SlotAvailability {
  /** The dateTime string identifying the slot */
  dateTime: string
  /** Whether the slot is available for booking */
  isAvailable: boolean
  // TBC: do we need loading state?
}

/**
 * Parameter types for strategy hooks
 */
export interface UseAvailableTimeSlotsParams {
  /** Date index for multi-date scenarios (default: 0) */
  dateIndex?: number
  /** Query function provided by app (returns query options) - OPTIONAL */
  slotAvailabilityQueryFn?: SlotAvailabilityQueryFn
}

export interface UseSlotValidationParams {
  /** Query function provided by app (returns query options) - optional */
  slotValidationQueryFn?: SlotValidationQueryFn
}

export interface UseSlotValidationMutationParams {
  /** Mutation function that validates a single slot - provided by app */
  slotValidationMutationFn?: (params: SingleSlotParams) => Promise<SlotValidationResponse>
}

/**
 * Result type for slot validation mutation
 * Single mutation that can validate one or multiple slots
 * Params are built internally using useSlotRequest/useSlotRequests
 */
export interface SlotValidationMutationResult<TResult = unknown> {
  /** Single mutation for batch validation - no params needed, built internally */
  mutation: UseMutationResult<TResult, unknown, void>
  /** Whether mutation should be triggered (validation is enabled and params are ready) */
  shouldValidate: boolean
  /** Current slot availability states for all slots in the form */
  slotAvailabilities: Array<{dateTime: string; isAvailable: boolean}>
}

export type SlotManagementContextProps = {
  /** Configuration for slot management (feature toggle, org unit, site) */
  config: SlotManagementConfig
  /** Slot availability state for each dateTime in the form */
  slotAvailabilities: SlotAvailability[]
  setSlotAvailabilities: (slotAvailabilities: SlotAvailability[]) => void
  setSlotAvailability: (options: SetSlotAvailabilityOptions) => void
  /** Strategy hooks - implementation depends on context (Order Change or Order Intake) */
  hooks: {
    useAvailableTimeSlots: (params: UseAvailableTimeSlotsParams) => AvailableTimeSlotsResult
    useSlotValidation: (params: UseSlotValidationParams) => SlotValidationResult[]
    useSlotValidationMutation: (
      params: UseSlotValidationMutationParams
    ) => SlotValidationMutationResult
  }
}

export const SlotManagementContext = createContext<SlotManagementContextProps | undefined>(
  undefined
)
